import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { darkenHexColor, lightenHexColor } from "../../utilities/parsers";

// All measurements below are in the SVG's own design-space units (the
// original artwork was authored on a 320x190 canvas with a 22px label).
// Everything scales off of that reference.
const BASE_FONT_SIZE = 22;
const DESIGN_WIDTH = 320;
const DESIGN_HEIGHT = 190;
const TEXT_CENTER_X = 170;
const TEXT_ROTATE_DEG = 5;

// The tag body's true horizontal midpoint (halfway between its left and right
// tips) vs. where the string/ring was drawn - used to recenter the hanger.
const TAG_CENTER_X = 174;
const STRING_ANCHOR_X = 160;

// The label reads a touch small at the badge's native display size, so its
// glyphs are drawn slightly boosted relative to BASE_FONT_SIZE - this only
// affects the text, the tag artwork keeps scaling strictly off BASE_FONT_SIZE.
const TEXT_SIZE_BOOST = 1.2;
const TEXT_FONT_SIZE = BASE_FONT_SIZE * TEXT_SIZE_BOOST;

// The tag body is a faceted octagon, not a simple ribbon rectangle - only the
// points on the right half (past the shape's own center) move with `delta`,
// the pointed left tip and its neighbours stay put.
const TEXT_SAFE_WIDTH = 150;
const SIDE_PADDING = 16;

// The badge only ever grows/shrinks in exact proportion to the configured
// font size (never distorts), clamped so a stray value can't blow up the layout.
const MIN_FONT_SCALE = 0.5;
const MAX_FONT_SCALE = 2.5;

// Native display size of the artwork at BASE_FONT_SIZE, before font scaling.
const DISPLAY_SCALE = 0.35;

// The whole tag is built from exactly two seed colors - every other tone
// (gradient stops, the string's shine, shadows) is derived from these via
// lighten/darken so the artwork stays a coherent 2-color palette.
const DEFAULT_PRIMARY_COLOR = "#151515";
const DEFAULT_SECONDARY_COLOR = "#ff7a0a";

const estimateTextWidth = (text: string) => text.length * TEXT_FONT_SIZE * 0.55;

type DiscountTagBadgeProps = {
    text: string;
    fontSize?: number;
    fontWeight?: string | number;
    fontStyle?: string;
    color?: string;
    primaryColor?: string;
    secondaryColor?: string;
    style?: CSSProperties;
    [dataAttr: `data-${string}`]: unknown;
};

const DiscountTagBadge = ({
    text,
    fontSize,
    fontWeight,
    fontStyle,
    color,
    primaryColor,
    secondaryColor,
    style,
    ...rest
}: DiscountTagBadgeProps) => {
    const uid = useId();
    const textRef = useRef<SVGTextElement>(null);
    const [textWidth, setTextWidth] = useState(() => estimateTextWidth(text || ""));

    const measure = () => {
        const node = textRef.current;
        if (!node) return;
        try {
            const box = node.getBBox();
            if (box.width > 0) setTextWidth(box.width);
        } catch {
            // node not measurable yet (e.g. not laid out) - keep last known width
        }
    };

    useLayoutEffect(() => {
        measure();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text, fontWeight, fontStyle]);

    useEffect(() => {
        const fonts = (document as any)?.fonts;
        if (fonts?.ready) {
            fonts.ready.then(measure).catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text, fontWeight, fontStyle]);

    if (!text) return null;

    const safeFontSize = fontSize && fontSize > 0 ? fontSize : BASE_FONT_SIZE;
    const fontScale = Math.min(MAX_FONT_SCALE, Math.max(MIN_FONT_SCALE, safeFontSize / BASE_FONT_SIZE));

    const delta = Math.max(0, textWidth + SIDE_PADDING * 2 - TEXT_SAFE_WIDTH);
    const designWidth = DESIGN_WIDTH + delta;

    const finalScale = DISPLAY_SCALE * fontScale;
    const renderedWidth = designWidth * finalScale;
    const renderedHeight = DESIGN_HEIGHT * finalScale;
    const textX = TEXT_CENTER_X + delta / 2;

    const stringId = `tag-string-${uid}`;
    const orangeId = `tag-orange-${uid}`;
    const faceId = `tag-face-${uid}`;
    const shadowId = `tag-shadow-${uid}`;

    const primary = primaryColor || DEFAULT_PRIMARY_COLOR;
    const secondary = secondaryColor || DEFAULT_SECONDARY_COLOR;

    // Neutral family: the face gradient, the tag's cast shadow, the string's
    // black outline, and the ring all read off these two tones.
    const primaryLight = lightenHexColor(primary, 8);
    const primaryDark = darkenHexColor(primary, 7);
    // The string's metallic shine is just `primary` pushed a long way toward white.
    const stringBase = lightenHexColor(primary, 27);
    const stringHighlight = lightenHexColor(primary, 82);
    const stringMid = lightenHexColor(primary, 42);

    // Accent family: the border gradient and every orange highlight/stroke.
    const secondaryLight = lightenHexColor(secondary, 35);
    const secondaryDark = darkenHexColor(secondary, 27);

    return (
        <svg
            {...rest}
            width={renderedWidth}
            height={renderedHeight}
            viewBox={`0 0 ${designWidth} ${DESIGN_HEIGHT}`}
            style={{
                position: "absolute",
                top: -2,
                right: -6,
                overflow: "visible",
                zIndex: 10,
                ...style,
            }}
            role="img"
            aria-label={text}
        >
            <defs>
                <linearGradient id={faceId} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={primaryLight} />
                    <stop offset="45%" stopColor={primary} />
                    <stop offset="100%" stopColor={primaryDark} />
                </linearGradient>
                <linearGradient id={orangeId} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={secondaryLight} />
                    <stop offset="40%" stopColor={secondary} />
                    <stop offset="100%" stopColor={secondaryDark} />
                </linearGradient>
                <linearGradient id={stringId} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={stringBase} />
                    <stop offset="35%" stopColor={stringHighlight} />
                    <stop offset="55%" stopColor={stringMid} />
                    <stop offset="100%" stopColor={primaryLight} />
                </linearGradient>
                <filter id={shadowId} x="-30%" y="-30%" width="170%" height="190%">
                    <feDropShadow dx="0" dy="9" stdDeviation="5" floodColor="#000" floodOpacity=".65" />
                </filter>
            </defs>

            {/* Hanging string + ring: recentered over the tag's true midpoint as it grows */}
            <g transform={`translate(${TAG_CENTER_X - STRING_ANCHOR_X + delta / 2} 0)`}>
                <path
                    d="M160 7C160 7 158 22 145 34C132 46 116 52 103 58"
                    fill="none"
                    stroke={primaryDark}
                    strokeWidth={8}
                    strokeLinecap="round"
                />
                <path
                    d="M160 7C160 7 158 22 145 34C132 46 116 52 103 58"
                    fill="none"
                    stroke={`url(#${stringId})`}
                    strokeWidth={4}
                    strokeLinecap="round"
                />
                <path
                    d="M160 7C160 7 160 18 167 28C174 38 185 45 197 51"
                    fill="none"
                    stroke={primaryDark}
                    strokeWidth={8}
                    strokeLinecap="round"
                />
                <path
                    d="M160 7C160 7 160 18 167 28C174 38 185 45 197 51"
                    fill="none"
                    stroke={`url(#${stringId})`}
                    strokeWidth={4}
                    strokeLinecap="round"
                />
                <circle cx="160" cy="8" r="6" fill={primary} stroke={secondary} strokeWidth={2} />
            </g>

            {/* Tag body: a faceted octagon - the left tip and its neighbours stay put,
                the right tip and its neighbours move with the label via `delta` */}
            <path
                d={`M77 51L103 39L${263 + delta} 54L${284 + delta} 76L${270 + delta} 144L${247 + delta} 157L86 142L64 119Z`}
                fill={primaryDark}
                filter={`url(#${shadowId})`}
            />
            <path
                d={`M74 46L101 34L${258 + delta} 49L${278 + delta} 70L${264 + delta} 137L${244 + delta} 150L83 135L61 113Z`}
                fill={`url(#${orangeId})`}
            />
            <path
                d={`M80 52L104 42L${251 + delta} 56L${269 + delta} 73L${256 + delta} 130L${240 + delta} 141L88 127L69 109Z`}
                fill={`url(#${faceId})`}
                stroke={primaryDark}
                strokeWidth={4}
            />
            <path
                d={`M84 57L106 48L${247 + delta} 61L${262 + delta} 76L${250 + delta} 125L${237 + delta} 135L92 122L76 106Z`}
                fill="none"
                stroke={secondary}
                strokeWidth={2}
                opacity={0.9}
            />

            <g transform={`rotate(${TEXT_ROTATE_DEG} ${textX} 92)`}>
                <text
                    ref={textRef}
                    x={textX}
                    y={92}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={color || "#ffffff"}
                    fontSize={TEXT_FONT_SIZE}
                    fontWeight={fontWeight || 900}
                    fontStyle={fontStyle || "normal"}
                    letterSpacing={0.5}
                >
                    {text}
                </text>
            </g>

            {/* Underline flourish: recenters with the label instead of stretching */}
            <path
                d="M125 111L215 119"
                stroke={secondary}
                strokeWidth={3}
                strokeLinecap="round"
                transform={`translate(${delta / 2} 0)`}
            />
        </svg>
    );
};

export default DiscountTagBadge;
